'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: string;
  title: string;
  type: string;
  size: string;
  icon: string;
  description: string;
  downloadUrl: string;
}

interface TechnicalSpecsDownloadProps {
  resources: Resource[];
}

export default function TechnicalSpecsDownload({ resources }: TechnicalSpecsDownloadProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');

  const resourceTypes = ['all', ...Array.from(new Set(resources.map(r => r.type)))];

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || resource.type === selectedType;
    return matchesSearch && matchesType;
  });

  const handleDownload = (resource: Resource) => {
    // Mock download functionality
    console.log(`Downloading: ${resource.title}`);
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="DocumentTextIcon" size={28} variant="solid" className="text-primary" />
        <h3 className="font-display font-bold text-2xl text-primary">
          Technical Documentation Center
        </h3>
      </div>

      {/* Search and Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <Icon 
            name="MagnifyingGlassIcon" 
            size={20} 
            variant="outline" 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-surface border border-border rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {resourceTypes.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-4 py-2 rounded-md font-body text-sm transition-all duration-300 ${
                selectedType === type
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-surface text-foreground hover:bg-muted'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredResources.map((resource) => (
          <div
            key={resource.id}
            className="bg-surface rounded-lg p-4 border border-border hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-primary bg-opacity-10 flex items-center justify-center">
                <Icon name={resource.icon as any} size={24} variant="solid" className="text-primary" />
              </div>
              <span className="px-2 py-1 bg-accent bg-opacity-10 text-accent rounded text-xs font-body font-medium">
                {resource.type}
              </span>
            </div>

            <h4 className="font-display font-semibold text-base text-foreground mb-2">
              {resource.title}
            </h4>
            <p className="font-body text-xs text-muted-foreground mb-3 line-clamp-2">
              {resource.description}
            </p>

            <div className="flex items-center justify-between">
              <span className="font-body text-xs text-muted-foreground">{resource.size}</span>
              <button
                onClick={() => handleDownload(resource)}
                className="flex items-center space-x-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md font-cta text-xs hover:shadow-md transition-all duration-300"
              >
                <Icon name="ArrowDownTrayIcon" size={14} variant="solid" />
                <span>Download</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredResources.length === 0 && (
        <div className="text-center py-12">
          <Icon name="DocumentMagnifyingGlassIcon" size={48} variant="outline" className="text-muted-foreground mx-auto mb-4" />
          <p className="font-body text-muted-foreground">No resources found matching your criteria</p>
        </div>
      )}

      {/* Quick Access Links */}
      <div className="mt-8 pt-6 border-t border-border">
        <h4 className="font-display font-semibold text-sm text-foreground mb-4">Quick Access</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'API Reference', icon: 'CodeBracketIcon' },
            { label: 'SDK Downloads', icon: 'CubeIcon' },
            { label: 'Integration Guides', icon: 'BookOpenIcon' },
            { label: 'Video Tutorials', icon: 'PlayCircleIcon' },
          ].map((link, index) => (
            <button
              key={index}
              className="flex items-center space-x-2 p-3 bg-surface rounded-md hover:bg-muted transition-colors duration-300"
            >
              <Icon name={link.icon as any} size={18} variant="outline" className="text-accent" />
              <span className="font-body text-xs text-foreground">{link.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}